import { useEffect, useState } from "react";
import apiClient from "../../api/client";
import { ASSET_BASE_URL } from "../../config/env";
import { toast } from "react-toastify";

const ComboPlansModal = ({
  visible,
  onClose,
  userId,
  onSuccess
}) => {

  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const fetchCombos = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/customer/getComboPlans.php");

        if (data.success) {
          setCombos(data.data);
        } else {
          toast.error("Failed to load combos");
        }

      } catch (err) {
        console.error(err);
        toast.error("Error fetching combos");
      } finally {
        setLoading(false);
      }
    };

    fetchCombos();
  }, [visible]);

  // Apply full combo
  const handleSelectCombo = async (combo) => {
    setSelectedCombo(combo.id);
    setApplying(true);
    
    try {
      const requests = [];
      // 🔥 STEP 1: RESET ALL EXISTING PLANS
      await apiClient.post("/customer/reset_user_plans.php", {
        user_id: userId
      });

      // ✅ PlayBox
      if (combo.playboxtv?.id) {
        requests.push(apiClient.post("/customer/change_plan.php", {
          user_id: userId,
          plan_id: combo.playboxtv.id,
          type: "playboxtv"
        }));
      }

      // ✅ Watcho
      if (combo.watcho?.id) {
        requests.push(apiClient.post("/customer/change_plan.php", {
          user_id: userId,
          plan_id: combo.watcho.id,
          type: "watcho"
        }));

      }

      // apiClient (axios) rejects on any non-2xx response, so if we get
      // here every request in the combo actually succeeded.
      await Promise.all(requests);

      toast.success("🎉 Combo applied successfully!");
      onSuccess();
      setTimeout(() => onClose(), 1000);

    } catch (err) {
      console.error(err);
      toast.error("Failed to apply combo");
    } finally {
      setApplying(false);
      setSelectedCombo(null);
    }
  };

  // Render OTT icons
  const renderApps = (apps) => (
    <div className="combo-modal__ott-row">
      {apps?.map(app => (
        <div key={app} className="combo-modal__ott-icon-wrapper" title={app}>
          <img
            src={`${ASSET_BASE_URL}/${app.toLowerCase()}.png`}
            alt={app}
            className="combo-modal__ott-icon"
          />
        </div>
      ))}
    </div>
  );

  if (!visible) return null;

  return (
    <div className="combo-modal__overlay" onClick={onClose}>
      <div className="combo-modal__container" onClick={(e) => e.stopPropagation()}>
        <div className="combo-modal__header">
          <div className="combo-modal__header-left">
            <h3 className="combo-modal__title">Smart Combos</h3>
          </div>
          <button className="combo-modal__close-btn" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="combo-modal__loading">
            <div className="combo-modal__spinner"></div>
            <p>Finding the best combos for you...</p>
          </div>
        ) : (
          <div className="combo-modal__grid">
            {combos.length === 0 ? (
              <div className="combo-modal__empty">
                <div className="combo-modal__empty-icon">📦</div>
                <p>No combos available</p>
                <p className="combo-modal__empty-sub">Check back later for exciting offers!</p>
              </div>
            ) : (
              combos.map(combo => (
                <div
                  key={combo.id}
                  className={`combo-modal__card ${selectedCombo === combo.id ? 'combo-modal__card--applying' : ''}`}
                >
                  {combo.special_text && (
                    <div className="combo-modal__badge">
                      🔥 {combo.special_text}
                    </div>
                  )}

                  <div className="combo-modal__card-header">
                    <div className="combo-modal__price">
                      <span className="combo-modal__price-label">Total Price: </span>
                      <span className="combo-modal__price-value">₹{combo.total_price}</span>
                    </div>
                  </div>

                  <div className="combo-modal__plans">
                    {/* ✅ PLAYBOX */}
                    {combo.playboxtv && (
                      <div className="combo-modal__plan">
                        <div className="combo-modal__plan-header">
                          <div className="combo-modal__plan-info">
                            <span className="combo-modal__plan-name">PlayBox TV: </span>
                          </div>
                          <span className="combo-modal__plan-price">
                            ₹{combo.playboxtv.planPrice}
                          </span>
                        </div>
                        {renderApps(combo.playboxtv.ott)}
                      </div>
                    )}

                    {/* ✅ WATCHO */}
                    {combo.watcho && (
                      <div className="combo-modal__plan">
                        <div className="combo-modal__plan-header">
                          <div className="combo-modal__plan-info">
                            <span className="combo-modal__plan-name">Watcho: </span>
                          </div>
                          <span className="combo-modal__plan-price">
                            ₹{combo.watcho.planPrice}
                          </span>
                        </div>
                        {renderApps(combo.watcho.ott)}
                      </div>
                    )}

                  </div>

                  <button 
                    className="combo-modal__apply-btn"
                    onClick={() => handleSelectCombo(combo)}
                    disabled={applying && selectedCombo === combo.id}
                  >
                    {applying && selectedCombo === combo.id ? (
                      <>
                        <span className="combo-modal__btn-spinner"></span>
                        Applying...
                      </>
                    ) : (
                      'Apply Combo'
                    )}
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComboPlansModal;